/*
Generate a modern enterprise-grade Angular 17+ application layout using Angular Material and Tailwind CSS.

Requirements:
- Full responsive design (mobile, tablet, desktop)
- Minimal, clean UI with pharmaceutical / compliance theme
- Remove all unused menus, demo links, placeholder widgets from the existing template
- Sidebar navigation should include ONLY:
  - Dashboard
  - Bulk Upload
  - Records & QR Codes
  - Reports
  - User Management (Admin only)
- Top header with:
  - Application title
  - Logged-in user name
  - Role badge (Admin/User)
  - Logout button
- Role-based menu rendering
- Centralized error handling UI
- Loading states and empty state illustrations
- Clean folder structure using Angular best practices
- Use standalone components
- Follow accessibility (ARIA) standards
- No backend/API logic – UI only
*/

import { Component, signal, computed, OnInit } from '@angular/core';
import { RouterOutlet, Router, NavigationEnd } from '@angular/router';
import { CommonModule } from '@angular/common';

import { filter } from 'rxjs';
import { HeaderComponent } from './components/header/header.component';
import { ErrorBoundaryComponent } from './components/error-boundary/error-boundary.component';
import { SidebarComponent } from './components/sidebar/sidebar.component';
import { LoadingComponent } from './components/loading/loading.component';
import { LoaderService } from './services/loader.service';
import { DownloadToastComponent } from './components/download-toast/download-toast.component';

@Component({
  selector: 'app-root',
  imports: [CommonModule, RouterOutlet, SidebarComponent, HeaderComponent, ErrorBoundaryComponent, LoadingComponent, DownloadToastComponent],
  templateUrl: './app.html',
  styleUrl: './app.scss',
})
export class App implements OnInit {
  protected readonly title = signal('AskLab');
  isLoginPage = signal(false);
  isPublicPage = signal(false);

  constructor(
    private router: Router,
    public loaderService: LoaderService,
  ) {}

  ngOnInit() {
    // Seed signals from the current URL immediately so the correct layout
    // renders on initial page load / hard refresh — before any NavigationEnd fires.
    this.updatePageFlags(this.router.url);

    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: any) => {
        this.updatePageFlags(event.urlAfterRedirects);
      });
  }

  private updatePageFlags(url: string): void {
    this.isLoginPage.set(url === '/login' || url === '/');
    this.isPublicPage.set(url.startsWith('/product/'));
  }
}
